// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";

const categoryLabel: Record<string, string> = {
  registration: "Registration",
  payment: "Payment",
  invitation: "Invitations",
  operational: "Operational",
  authentication: "Authentication",
  admin_notification: "Admin Notifications",
  campaign: "Campaigns",
  utility: "Utility",
  roster: "Roster",
};

/** Cheap stable fingerprint of a template's editable content. */
const fingerprint = (t: any) => {
  const chans = [...(t.channels || [])]
    .sort((a: any, b: any) => String(a.channel).localeCompare(String(b.channel)))
    .map((c: any) =>
      [c.channel, c.is_enabled ? 1 : 0, c.subject || "", c.body_html || "", c.sms_body || ""].join("\u0001"),
    );
  return chans.join("\u0002");
};

const OrgEmailTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { scopeOrgId, createOrgId, isSuperAdmin, ready } = useAdminOrgScope();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Default the org filter to the admin's own org rather than every org.
  useEffect(() => {
    if (!ready || orgFilter) return;
    setOrgFilter(isSuperAdmin ? createOrgId || "all" : scopeOrgId || "all");
  }, [ready, isSuperAdmin, createOrgId, scopeOrgId, orgFilter]);

  const { data: orgs = [] } = useQuery({
    queryKey: ["org-template-orgs"],
    enabled: ready && isSuperAdmin,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("organizations")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgs || []).forEach((o: any) => (m[o.id] = o.name));
    return m;
  }, [orgs]);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["org-message-templates", scopeOrgId],
    enabled: ready,
    queryFn: async () => {
      let q = (supabase as any)
        .from("message_templates")
        .select(
          "id, key, name, description, category, is_active, organization_id, channels:message_template_channels(channel, is_enabled, subject, body_html, sms_body)",
        )
        .order("category", { ascending: true })
        .order("name", { ascending: true });
      if (scopeOrgId) q = q.eq("organization_id", scopeOrgId);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const distinctCategories = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t: any) => t.category && set.add(t.category));
    return Array.from(set).sort();
  }, [templates]);

  /** Per key: the most common fingerprint is treated as the seed/default. */
  const baselineByKey = useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    for (const t of templates) {
      const fp = fingerprint(t);
      counts[t.key] = counts[t.key] || {};
      counts[t.key][fp] = (counts[t.key][fp] || 0) + 1;
    }
    const base: Record<string, string> = {};
    for (const [key, m] of Object.entries(counts)) {
      base[key] = Object.entries(m).sort((a, b) => b[1] - a[1])[0][0];
    }
    return base;
  }, [templates]);

  const isCustomised = (t: any) =>
    Object.keys(baselineByKey).length > 0 && fingerprint(t) !== baselineByKey[t.key];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t: any) => {
      if (q) {
        const hay = `${t.name} ${t.key} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (orgFilter && orgFilter !== "all" && t.organization_id !== orgFilter) return false;
      return true;
    });
  }, [templates, search, categoryFilter, orgFilter]);

  const showAllOrgs = isSuperAdmin && orgFilter === "all";

  /** category -> rows. A row is a single template, or (all-orgs view) a key group. */
  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    if (!showAllOrgs) {
      for (const t of filtered) {
        const c = t.category || "other";
        (g[c] = g[c] || []).push({ kind: "single", id: t.id, template: t });
      }
      return g;
    }
    const byKey: Record<string, any[]> = {};
    for (const t of filtered) (byKey[t.key] = byKey[t.key] || []).push(t);
    for (const [key, items] of Object.entries(byKey)) {
      const c = items[0].category || "other";
      (g[c] = g[c] || []).push({
        kind: "group",
        id: key,
        key,
        name: items[0].name,
        items,
        customised: items.filter(isCustomised),
      });
    }
    return g;
  }, [filtered, showAllOrgs, baselineByKey]);

  const handleToggleActive = async (template: any, value: boolean) => {
    const { error } = await (supabase as any)
      .from("message_templates")
      .update({ is_active: value })
      .eq("id", template.id);
    if (error) {
      toast.error("Update failed: " + error.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["org-message-templates", scopeOrgId] });
  };

  const TemplateRow = ({ t, showOrg, indent }: any) => (
    <div
      className={`px-4 py-3 flex items-center gap-4 hover:bg-muted/30 transition-colors border-t border-border first:border-t-0 ${
        indent ? "pl-12 bg-muted/10" : ""
      }`}
    >
      <button
        type="button"
        onClick={() => navigate(`/admin-dashboard/communication/templates/${t.id}`)}
        className="flex-1 min-w-0 text-left"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium truncate">{t.name}</span>
          {showOrg && (
            <Badge variant="secondary" className="text-[10px]">
              {orgName[t.organization_id] || "Unknown org"}
            </Badge>
          )}
          {(t.channels || []).some((c: any) => c.channel === "email" && c.is_enabled) && (
            <Badge variant="outline" className="text-[10px]">Email</Badge>
          )}
          {(t.channels || []).some((c: any) => c.channel === "sms" && c.is_enabled) && (
            <Badge variant="outline" className="text-[10px]">SMS</Badge>
          )}
          {isCustomised(t) && (
            <Badge className="text-[10px] bg-amber-500 text-white hover:bg-amber-500">
              Customised
            </Badge>
          )}
          {!t.is_active && <Badge variant="secondary">Inactive</Badge>}
        </div>
        <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">{t.key}</p>
      </button>
      <div className="flex items-center gap-2 shrink-0">
        <Switch checked={!!t.is_active} onCheckedChange={(v) => handleToggleActive(t, v)} />
        <button
          type="button"
          onClick={() => navigate(`/admin-dashboard/communication/templates/${t.id}`)}
          className="text-sm text-primary hover:underline"
        >
          Edit
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Templates</h1>
        <p className="text-muted-foreground">
          Edit your organization's automated message templates. Each template can have an
          email and/or SMS version controlling subject, body, branding, banners and SMS text.
        </p>
        {isSuperAdmin && (
          <p className="text-sm text-muted-foreground mt-1">
            {showAllOrgs
              ? "Viewing all organizations — templates are grouped by key."
              : `Viewing ${orgName[orgFilter] || "your organization"}'s templates (platform admin).`}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input
          placeholder="Search by name, key, description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger>
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {distinctCategories.map((c) => (
              <SelectItem key={c} value={c}>
                {categoryLabel[c] || c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isSuperAdmin && (
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o: any) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))}
              <SelectItem value="all">All organizations</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {!ready || isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading templates...
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          No templates found for this organization.
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border">
          {Object.entries(grouped).map(([cat, rows]) => (
            <div key={cat}>
              <div className="bg-muted/50 px-4 py-2">
                <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
                  {categoryLabel[cat] || cat} ({rows.length})
                </span>
              </div>
              {rows.map((row: any) =>
                row.kind === "single" ? (
                  <TemplateRow key={row.id} t={row.template} showOrg={false} />
                ) : (
                  <div key={row.id}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded((p) => ({ ...p, [row.id]: !p[row.id] }))
                      }
                      className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors border-t border-border first:border-t-0"
                    >
                      {expanded[row.id] ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium truncate">{row.name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {row.items.length} org{row.items.length === 1 ? "" : "s"}
                          </Badge>
                          {row.customised.length > 0 ? (
                            <Badge className="text-[10px] bg-amber-500 text-white hover:bg-amber-500">
                              {row.customised.length} customised
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px]">
                              none customised
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                          {row.key} · {row.items
                            .map((t: any) => orgName[t.organization_id] || "Unknown org")
                            .join(", ")}
                        </p>
                      </div>
                    </button>
                    {expanded[row.id] &&
                      row.items.map((t: any) => (
                        <TemplateRow key={t.id} t={t} showOrg indent />
                      ))}
                  </div>
                ),
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrgEmailTemplatesPage;
